/* eslint-disable */
import * as React from "react";
import { useTranslation } from "react-i18next";
import { FormattedNumber } from "../../../FormattedNumber";
import { BackgammonTableType } from "../../../../graphql/generated";
import { TableDataDefinition } from "../../types";
import { getEntryFee, getRequiredPlayers } from "../utils";
import { SectionTableFragmentFragment } from "../../../../graphql/SectionTableFragment.generated";
import { Column } from "react-table";
import { CubeValue, MAX_LOSER_MULTIPLIER } from "../Game/types";

const CUBE_NAMES = [
  CubeValue.ONE,
  CubeValue.TWO,
  CubeValue.FOUR,
  CubeValue.EIGHT,
  CubeValue.SIXTEEN,
  CubeValue.THIRTY_TWO,
  CubeValue.SIXTY_FOUR,
];
const CUBE_VALUES = [1, 2, 4, 8, 16, 32, 64];

const getMaxBet = (
  usingDoublingCube: boolean,
  bet: number,
  CubeValue: CubeValue
) => {
  if (usingDoublingCube) {
    const index = CUBE_NAMES.indexOf(CubeValue);
    const cubeValue = index !== -1 ? CUBE_VALUES[index] : 1;
    return bet * cubeValue * MAX_LOSER_MULTIPLIER;
  }
  return bet;
};

type TableType = Omit<SectionTableFragmentFragment, "metaData"> & {
  metaData: {
    points: number;
    turnTime: number;
    bet: number;
    type: BackgammonTableType;
    maxCubeValue: CubeValue;
    usingDoublingCube: boolean;
  };
};

type RowData = {
  users: number;
  points: number;
  turnTime: number;
  bet: number;
  maxBet: number;
  rounds: number;
};

export const tableData: TableDataDefinition<RowData> = {
  useColumns: (isTournament: boolean) => {
    const { t } = useTranslation();

    return React.useMemo(() => {
      let result: Array<Column<RowData>> = [
        {
          Header: t<string>("turn_time"),
          accessor: "turnTime",
          Cell: ({ row }) => <FormattedNumber value={row.original.turnTime} />,
        },
      ];

      if (!isTournament)
        result = result
          .concat([
            {
              Header: t<string>("bet"),
              accessor: "bet",
              Cell: ({ row }) => <FormattedNumber value={row.original.bet} />,
            },
          ])
          .concat([
            {
              Header: t<string>("max_win"),
              accessor: "maxBet",
              Cell: ({ row }) => (
                <FormattedNumber value={row.original.maxBet} />
              ),
            },
          ]);

      return result;
    }, [isTournament, t]);
  },
  makeRow: (table: TableType) => {
    return {
      users: table.players.length,
      points: table.metaData.points,
      turnTime: table.metaData.turnTime,
      bet: table.metaData.bet,
      maxBet: getMaxBet(
        table.metaData.usingDoublingCube,
        table.metaData.bet,
        table.metaData.maxCubeValue
      ),
      rounds: table.metaData.points,
    };
  },
  calculateEntryFee: (table) =>
    table.metaData.type === BackgammonTableType.Private
      ? null
      : table.metaData.bet * 2,

  getRequiredPlayers: (table) => getRequiredPlayers(table.metaData.players),
  getDetails: (table: TableType) => {
    const { t } = useTranslation();
    return (
      <>
        <div className="flex justify-between -mx-2">
          <div className="px-2">{t("backgammon.points")}</div>
          <div className="px-2">
            <FormattedNumber value={table.metaData.points} />
          </div>
        </div>
        <div className="flex justify-between -mx-2">
          <div className="px-2">{t("turn_time")}</div>
          <div className="px-2">
            <FormattedNumber value={table.metaData.turnTime} />
          </div>
        </div>
        <div className="flex justify-between -mx-2">
          <div className="px-2">{t("bet")}</div>
          <div className="px-2">
            <FormattedNumber value={table.metaData.bet} />
          </div>
        </div>
        {table.metaData.usingDoublingCube && (
          <div className="flex justify-between -mx-2">
            <div className="px-2">{t("max bet")}</div>
            <div className="px-2">
              <FormattedNumber
                value={getMaxBet(
                  table.metaData.usingDoublingCube,
                  table.metaData.bet,
                  table.metaData.maxCubeValue
                )}
              />
            </div>
          </div>
        )}
      </>
    );
  },
};
